/**
 * Lead Capture Form - Shows on tag view pages to collect visitor info
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, User, Phone, X, Send, Check, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadCaptureForm = ({ 
  nfcCode, 
  settings, 
  onClose, 
  onSuccess,
  theme = {}
}) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    custom: {}
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/leads/capture`, {
        nfc_code: nfcCode,
        email: formData.email,
        name: formData.name || null,
        phone: formData.phone || null,
        custom_fields: Object.keys(formData.custom).length > 0 ? formData.custom : null
      });

      setSubmitted(true);
      setTimeout(() => {
        onSuccess?.();
      }, 2000);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Lead capture error:', err);
    } finally {
      setLoading(false);
    }
  };

  const requiredFields = settings?.required_fields || ['email'];
  const customFields = settings?.custom_fields || [];
  const buttonText = settings?.button_text || 'Stay Connected';
  const successMessage = settings?.success_message || 'Thanks for connecting!';

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-6 shadow-xl text-center max-w-sm mx-auto"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <Check className="w-8 h-8 text-green-600" />
        </motion.div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{successMessage}</h3>
        <p className="text-gray-500 text-sm">We'll keep you updated.</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="bg-white rounded-2xl p-6 shadow-xl max-w-sm mx-auto relative"
    >
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      <div className="text-center mb-5">
        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-3">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Stay Connected</h3>
        <p className="text-gray-500 text-sm mt-1">Get updates and exclusive content</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Email - Always shown */}
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="email"
            placeholder="Email address"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Name - Optional based on settings */}
        {requiredFields.includes('name') && (
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Your name"
              required={requiredFields.includes('name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
            />
          </div>
        )}

        {/* Phone - Optional based on settings */}
        {requiredFields.includes('phone') && (
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="tel"
              placeholder="Phone number"
              required={requiredFields.includes('phone')}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
            />
          </div>
        )}

        {/* Custom fields */}
        {customFields.map((field, idx) => (
          <input
            key={idx}
            type="text"
            placeholder={field.label}
            required={field.required}
            value={formData.custom[field.name] || ''}
            onChange={(e) => setFormData({
              ...formData,
              custom: { ...formData.custom, [field.name]: e.target.value }
            })}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
          />
        ))}

        {error && (
          <p className="text-red-500 text-sm text-center">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Send className="w-4 h-4" />
              {buttonText}
            </>
          )}
        </button>

        <p className="text-xs text-gray-400 text-center">
          We respect your privacy. Unsubscribe anytime.
        </p>
      </form>
    </motion.div>
  );
};

export default LeadCaptureForm;
